import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/date';
import { Filter } from 'lucide-react';
import { DaySheetlyDate } from '@/components/fields/DaySheetlyDate';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';

export interface DaySheetlyFilters {
  startDate: string;
  endDate: string;
  projectId: string | null;
  billingStatus: string | null;
}

interface FilterOption {
  id: string;
  name: string;
}

interface DaySheetlyFiltersProps {
  filters: DaySheetlyFilters;
  defaultDateRange: Pick<DaySheetlyFilters, 'startDate' | 'endDate'>;
  onChange: (filters: DaySheetlyFilters) => void;
  projects: FilterOption[];
  showLabel?: boolean;
  popoverContainer?: HTMLElement | null;
  onDatePopoverOpenChange?: (open: boolean) => void;
}

export function formatDaySheetlyRange(start: string, end: string): string {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  const startFmt = sameMonth
    ? format(startDate, 'MMM d')
    : format(startDate, 'MMM d, yyyy');
  const endFmt = format(endDate, 'MMM d, yyyy');
  return `${startFmt} - ${endFmt}`;
}

export function DaySheetlyFiltersComponent({
  filters,
  defaultDateRange,
  onChange,
  projects,
  showLabel = true,
  popoverContainer,
  onDatePopoverOpenChange,
}: DaySheetlyFiltersProps) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap">
      {showLabel ? (
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters</span>
        </div>
      ) : null}

      <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
        <DaySheetlyDate
          mode="range"
          value={{
            from: filters.startDate || null,
            to: filters.endDate || null,
          }}
          onChange={(range) =>
            onChange({
              ...filters,
              startDate: range.from ?? '',
              endDate: range.to ?? '',
            })
          }
          placeholder="Select date range"
          clearable={false}
          resetValue={{
            from: defaultDateRange.startDate,
            to: defaultDateRange.endDate,
          }}
          showActions
          popoverContainer={popoverContainer}
          onOpenChange={onDatePopoverOpenChange}
          testId="date-range-filter"
          className="w-full sm:w-auto"
        />

        <Select
          value={filters.projectId ?? 'all'}
          onValueChange={(value) =>
            onChange({ ...filters, projectId: value === 'all' ? null : value })
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
